import { Component, OnDestroy, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { AiService, AiAnalysisRequest } from 'src/app/_services/ai.service';
import { DlpReportService } from 'src/app/_services/dlpReport.service';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';

interface AnalysisOption {
  key: string;
  label: string;
  icon: string;
  description: string;
}

@Component({
  selector: 'app-ai-analysis-modal',
  templateUrl: './ai-analysis-modal.component.html',
  styleUrls: ['./ai-analysis-modal.component.css']
})
export class AiAnalysisModalComponent implements OnDestroy {
  @Input() filters: any = {};

  selectedOption: AnalysisOption | null = null;
  isStreaming = false;
  isDone = false;
  streamStatus = '';
  rawMarkdown = '';
  renderedHtml: SafeHtml = '';
  private streamSub?: Subscription;
  private pendingMarkdownChunk = '';
  private renderTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly renderIntervalMs = 80;

  detailPanelOpen = false;
  detailPanelMail = '';
  detailPanelRows: any[] = [];
  detailPanelLoading = false;
  private detailSub?: Subscription;

  readonly options: AnalysisOption[] = [
    {
      key: 'evolution',
      label: 'ai.option.evolution',
      icon: 'las la-chart-line',
      description: 'ai.option.evolutionDesc'
    },
    {
      key: 'personal',
      label: 'ai.option.personal',
      icon: 'las la-user-shield',
      description: 'ai.option.personalDesc'
    },
    {
      key: 'financial',
      label: 'ai.option.financial',
      icon: 'las la-money-bill',
      description: 'ai.option.financialDesc'
    },
    {
      key: 'health',
      label: 'ai.option.health',
      icon: 'las la-heartbeat',
      description: 'ai.option.healthDesc'
    },
    {
      key: 'risk-sends',
      label: 'ai.option.riskSends',
      icon: 'las la-paper-plane',
      description: 'ai.option.riskSendsDesc'
    },
    {
      key: 'out-of-control',
      label: 'ai.option.outOfControl',
      icon: 'las la-exclamation-triangle',
      description: 'ai.option.outOfControlDesc'
    }
  ];

  constructor(
    public activeModal: NgbActiveModal,
    private aiService: AiService,
    private dlpReportService: DlpReportService,
    private sanitizer: DomSanitizer,
    private translate: TranslateService,
    private toastr: ToastrService
  ) {}

  selectOption(option: AnalysisOption) {
    this.selectedOption = option;
    this.rawMarkdown = '';
    this.renderedHtml = '';
    this.streamStatus = '';
    this.isDone = false;
    this.runAnalysis();
  }

  runAnalysis() {
    if (!this.selectedOption) return;

    this.closeDetailPanel();
    this.isStreaming = true;
    this.isDone = false;
    this.rawMarkdown = '';
    this.renderedHtml = '';
    this.pendingMarkdownChunk = '';
    this.clearRenderTimer();
    this.streamStatus = 'Iniciando analisis...';

    const toStr = (v: any): string | undefined => {
      if (v == null) return undefined;
      const arr: any[] = Array.isArray(v) ? v : [v];
      const flat: any[] = arr.reduce((acc: any[], x: any) => acc.concat(Array.isArray(x) ? x : [x]), []);
      const s = flat.filter((x: any) => x != null).join(',');
      return s || undefined;
    };

    const request: AiAnalysisRequest = {
      analysisType: this.selectedOption.key,
      language: this.translate.currentLang || 'es',
      mail: this.filters?.mail,
      action: toStr(this.filters?.type),
      department: toStr(this.filters?.department),
      priority: toStr(this.filters?.policy),
      sensitiveCount: this.filters?.sensitiveCount ?? undefined,
      externalDelivery: this.filters?.externalDelivery,
      infoLeak: this.filters?.infoLeak,
      dateBegin: this.filters?.dateBegin,
      dateEnd: this.filters?.dateEnd,
    };

    this.streamSub = this.aiService.streamAnalysis(request).subscribe({
      next: chunk => {
        if (chunk.startsWith('[STATUS]')) {
          this.streamStatus = chunk.replace('[STATUS]', '').trim();
          return;
        }

        if (chunk.startsWith('⏳')) {
          this.streamStatus = chunk.replace('⏳', '').trim();
          return;
        }

        this.enqueueMarkdownChunk(chunk);
      },
      error: err => {
        this.isStreaming = false;
        this.isDone = true;
        this.streamStatus = 'Error en el analisis';
        this.flushPendingMarkdown();
        this.rawMarkdown += `\n\nError: ${err}`;
        this.updateRenderedHtml();
      },
      complete: () => {
        this.isStreaming = false;
        this.isDone = true;
        this.streamStatus = '';
        this.flushPendingMarkdown();
        this.updateRenderedHtml();
      }
    });
  }

  exportPdf() {
    if (!this.rawMarkdown) return;
    const optionLabel = this.translate.instant(this.selectedOption?.label ?? '');
    const cleanedMarkdown = this.rawMarkdown
      .replace(/⏳/g, '')
      .replace(/\*\*Analizando[^\n]*\*\*/g, '')
      .replace(/\[\[EMAIL:[^\]]+\]\]/g, '')
      .replace(/^\s*\n/gm, '\n')
      .trimStart();
    const content = this.markdownToHtml(cleanedMarkdown);
    const date = new Date().toLocaleDateString('es-ES');

    const printWindow = window.open('', '_blank', 'width=1000,height=700');
    if (!printWindow) return;
    printWindow.document.write(`<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <title>Análisis IA - DLP - ${optionLabel}</title>
  <style>
    @page { size: A4 landscape; margin: 10mm; }
    body { font-family: Arial, sans-serif; padding: 24px; font-size: 12px; color: #222; }
    h1 { color: #1a237e; font-size: 20px; border-bottom: 2px solid #1a237e; padding-bottom: 8px; margin-bottom: 4px; }
    h2 { color: #283593; font-size: 15px; margin-top: 4px; }
    h3, h4, h5 { color: #3949ab; }
    p { color: #444; line-height: 1.6; }
    .meta { color: #888; font-size: 11px; margin-bottom: 20px; }
    table { width: 100%; border-collapse: collapse; margin: 14px 0; page-break-inside: auto; table-layout: fixed; font-size: 9px; }
    th { background: #1a237e; color: white; padding: 4px 6px; text-align: left; font-weight: 600; border: 1px solid #283593; word-wrap: break-word; overflow-wrap: break-word; }
    td { padding: 4px 6px; border: 1px solid #ddd; vertical-align: top; word-wrap: break-word; overflow-wrap: break-word; }
    tr:nth-child(even) td { background: #f5f7ff; }
    tr { page-break-inside: avoid; }
    @media print { button { display: none; } }
  </style>
</head>
<body>
  <h1>Análisis IA · DLP</h1>
  <h2>${optionLabel}</h2>
  <p class="meta">Generado el ${date} · Safecloud Pro</p>
  ${content}
  <script>window.onload = function() { window.print(); }<\/script>
</body>
</html>`);
    printWindow.document.close();
  }

  goBack() {
    this.streamSub?.unsubscribe();
    this.clearRenderTimer();
    this.selectedOption = null;
    this.isStreaming = false;
    this.isDone = false;
    this.rawMarkdown = '';
    this.renderedHtml = '';
    this.pendingMarkdownChunk = '';
    this.streamStatus = '';
    this.closeDetailPanel();
  }

  close() {
    this.streamSub?.unsubscribe();
    this.detailSub?.unsubscribe();
    this.clearRenderTimer();
    this.activeModal.close();
  }

  ngOnDestroy() {
    this.streamSub?.unsubscribe();
    this.detailSub?.unsubscribe();
    this.clearRenderTimer();
  }

  onResultClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const btn = target?.closest('[data-ai-mail]') as HTMLElement | null;
    if (!btn) return;

    const mail = btn.getAttribute('data-ai-mail');
    if (mail) this.openDetailPanel(mail);
  }

  openDetailPanel(mail: string): void {
    this.detailPanelOpen = true;
    this.detailPanelMail = mail;
    this.detailPanelLoading = true;
    this.detailPanelRows = [];

    const filters: any = { mail };
    if (this.filters?.dateBegin) filters.dateBegin = this.filters.dateBegin;
    if (this.filters?.dateEnd) filters.dateEnd = this.filters.dateEnd;

    // Map analysisType to typeGroup for backend filtering
    if (this.selectedOption?.key) {
      const typeGroupMap: { [key: string]: string } = {
        'financial': 'Financial,Financial-Bank',
        'personal': 'PII-Identity,PII-Driver,PII-Company',
        'health': 'Health,Health-SSN'
      };
      const typeGroup = typeGroupMap[this.selectedOption.key];
      if (typeGroup) {
        filters.typeGroup = typeGroup;
      }
    }

    this.detailSub?.unsubscribe();
    this.detailSub = this.dlpReportService.getAllDlpReports(1, 9999, filters).subscribe({
      next: res => {
        this.detailPanelRows = (res.body as any)?.results || [];
        this.detailPanelLoading = false;
      },
      error: () => {
        this.detailPanelLoading = false;
      }
    });
  }

  closeDetailPanel(): void {
    this.detailSub?.unsubscribe();
    this.detailPanelOpen = false;
    this.detailPanelMail = '';
    this.detailPanelRows = [];
    this.detailPanelLoading = false;
  }

  private enqueueMarkdownChunk(chunk: string): void {
    if (!chunk) return;

    this.pendingMarkdownChunk += chunk;
    if (this.renderTimer) return;

    this.renderTimer = setTimeout(() => {
      this.flushPendingMarkdown();
      this.updateRenderedHtml();
    }, this.renderIntervalMs);
  }

  private flushPendingMarkdown(): void {
    if (!this.pendingMarkdownChunk) {
      this.clearRenderTimer();
      return;
    }

    this.clearRenderTimer();
    this.rawMarkdown += this.pendingMarkdownChunk;
    this.pendingMarkdownChunk = '';
  }

  private clearRenderTimer(): void {
    if (!this.renderTimer) return;
    clearTimeout(this.renderTimer);
    this.renderTimer = null;
  }

  private updateRenderedHtml(): void {
    this.renderedHtml = this.sanitizer.bypassSecurityTrustHtml(
      this.markdownToHtml(this.rawMarkdown)
    );
  }

  private markdownToHtml(text: string): string {
    if (!text) return '';

    const lines = text.split('\n');
    let html = '';
    let inTable = false;
    let tbodyOpened = false;
    let headerColCount = 0;
    let pendingHeaderCells: string[] | null = null;

    for (const line of lines) {
      const trimmed = line.trim();
      const isTableRow = trimmed.startsWith('|') && trimmed.endsWith('|');
      const isSeparator = isTableRow && /^[\|\-\s:]+$/.test(trimmed);

      if (isTableRow) {
        const cells = trimmed.split('|').filter(c => c.trim() !== '');

        if (!inTable) {
          if (!isSeparator && pendingHeaderCells === null) {
            pendingHeaderCells = cells;
            continue;
          }

          if (isSeparator && pendingHeaderCells !== null) {
            inTable = true;
            headerColCount = pendingHeaderCells.length;
            html += '<table class="ai-table"><thead><tr>';
            for (const c of pendingHeaderCells) html += `<th>${this.processInlineMarkdown(c.trim())}</th>`;
            html += '</tr></thead><tbody>';
            tbodyOpened = true;
            pendingHeaderCells = null;
            continue;
          }

          continue;
        }

        if (isSeparator) {
          continue;
        }

        if (!tbodyOpened) {
          tbodyOpened = true;
          html += '<tbody>';
        }

        const rowCells = cells.slice(0, headerColCount);
        html += '<tr>';
        for (let i = 0; i < headerColCount; i++) {
          const c = (rowCells[i] ?? '').trim();
          html += `<td>${this.renderCell(c)}</td>`;
        }
        html += '</tr>';
      } else {
        if (pendingHeaderCells !== null) {
          html += `<p>${this.processInlineMarkdown(pendingHeaderCells.join(' | '))}</p>`;
          pendingHeaderCells = null;
        }

        if (inTable) {
          html += '</tbody></table>';
          inTable = false;
          tbodyOpened = false;
        }
        if (/^[-*_]{3,}$/.test(trimmed)) {
          html += '<hr>';
        } else if (trimmed.startsWith('### ')) {
          html += `<h5>${this.escapeHtml(trimmed.substring(4))}</h5>`;
        } else if (trimmed.startsWith('## ')) {
          html += `<h4>${this.escapeHtml(trimmed.substring(3))}</h4>`;
        } else if (trimmed.startsWith('# ')) {
          html += `<h3>${this.escapeHtml(trimmed.substring(2))}</h3>`;
        } else if (trimmed) {
          html += `<p>${this.processInlineMarkdown(trimmed)}</p>`;
        }
      }
    }

    if (pendingHeaderCells !== null) {
      html += `<p>${this.processInlineMarkdown(pendingHeaderCells.join(' | '))}</p>`;
    }

    if (inTable) html += '</tbody></table>';
    return html;
  }

  private renderCell(text: string): string {
    const emailMatch = /\[\[EMAIL:([^\]]+)\]\]/.exec(text);
    if (!emailMatch) return this.processInlineMarkdown(text);

    const mail = emailMatch[1].trim();
    const cleanText = text.replace(emailMatch[0], '').trim();
    return `${this.processInlineMarkdown(cleanText)} ` +
      `<button type="button" class="ai-detail-btn" data-ai-mail="${this.escapeHtml(mail)}" title="${this.escapeHtml(mail)}">` +
      `<i class="las la-search-plus"></i></button>`;
  }

  private processInlineMarkdown(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>');
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }
}
